---
title: "Handling lost or damaged books"
slug: "handling-lost-or-damaged-books"
category: "books"
description: "Mark a book as lost, damaged, or written off, and keep your records honest without drama."
tags: ["lost", "damaged", "written off", "discard", "books"]
order: 3
---

Every school library loses a few books a year. A pupil moves, a book comes back with a ripped spine, a reading scheme set goes out of print and you'd rather just remove the entire thing from the catalogue. BookBurrow has three different ways to handle these situations, each suited to a different case.

## The three states: Lost, Damaged, Written off

- **Lost** — the book was on loan and hasn't come back. The borrower record still exists; the book stays in the catalogue but is no longer available to lend.
- **Damaged** — the book is back, but it's unusable. You want a note on the record (and ideally on reports) but you're going to remove the physical copy from the shelves.
- **Written off** — the book is gone from your library entirely, for any reason. Its record is archived but not deleted, so historical reports still know it existed.

Keeping records of all three means you can run a "books we've lost this year" report and have a sensible conversation with your head teacher about replacement budget.

## Marking a book as lost

This is usually the outcome of end-of-term chasing.

<Steps>
  <Step title="Find the book">
    Search for it in the Books tab, or find the loan from the borrower's record.
  </Step>
  <Step title="Open the loan">
    Click the loan entry to see details (borrower, date out, due date).
  </Step>
  <Step title="Click Mark as lost">
    You'll be asked for an optional reason. Common choices: "pupil moved schools", "never returned after Easter", "cannot find despite checking shelves".
  </Step>
  <Step title="Confirm">
    The book's status changes to Lost, the borrower's active loans count goes down, and the loan is recorded as ending with a lost outcome.
  </Step>
</Steps>

<Callout variant="tip" title="Found again later?">
If a lost book turns up — under a coat peg, in a drawer, in the back of a car — use **Books → Found (undo lost)** to restore it. The loan history is kept so you can still see that it was lost for a time.
</Callout>

## Marking a book as damaged

Use this when a returned book is not fit to go back on the shelves.

<Steps>
  <Step title="Scan the return as normal">
    On the Return tab, scan the book's barcode. The loan is closed.
  </Step>
  <Step title="Click the Damaged button">
    It appears in the return confirmation message.
  </Step>
  <Step title="Choose a severity">
    - **Repairable** — will be fixed and returned to shelves. The book stays Available.
    - **Write off** — the book is beyond repair. See the next section.
  </Step>
  <Step title="Add a note">
    Useful for end-of-year reports: "water damage", "spine broken", "drawn in with biro".
  </Step>
</Steps>

## Writing off books in bulk

At the end of a reading scheme's life, you might want to remove two hundred books at once — typically an old set that's been replaced by a newer edition.

<Steps>
  <Step title="Filter the catalogue">
    Search for the books you want to write off (e.g. filter by reading scheme).
  </Step>
  <Step title="Select all">
    Tick the checkbox at the top of the list to select every filtered book.
  </Step>
  <Step title="Click Bulk actions → Write off">
    Give a reason (e.g. "Scheme replaced — July 2026").
  </Step>
  <Step title="Confirm">
    BookBurrow moves the entire set into an archive state. They stop showing up in the catalogue but stay in the database for historical reporting.
  </Step>
</Steps>

## Reporting on losses and damage

For the full rundown of what BookBurrow can produce on this front, see [Running reports](/help/guides/running-reports). The short version: **Reports → Loss and damage** gives you a year-to-date summary of:

- Books marked lost, with the cost of replacement (if you've set prices).
- Books marked damaged, grouped by severity.
- Books written off, grouped by reason.

This is exactly the kind of report that makes a library budget conversation with senior leadership short and unambiguous.

If a book you marked as lost turns out to have been accidentally deleted instead, you may be able to [restore from a recent backup](/help/faq#lost-backup) rather than re-adding it by hand.
